import React, { useLayoutEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(el => {
    if (el.closest('[aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return true;
  });
}

export type ModalFocusRootProps = {
  ariaLabel: string;
  children?: any;
};

/**
 * Focus trap, Tab cycle, restore focus, aria-modal dialog semantics.
 * Renders {@link ModalFocusRootLayout} (styled) inside a modal shell.
 */
export function ModalFocusRoot({ ariaLabel, children }: ModalFocusRootProps) {
  const rootRef = useRef(null) as any;
  const hasInitialFocusRef = useRef(false);

  useLayoutEffect(() => {
    const trap = rootRef.current as HTMLElement | null;
    if (!trap) return undefined;

    const previousActive = document.activeElement as HTMLElement | null;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusables = getFocusableElements(trap);
      
      // On first Tab press, focus the first element if no element is focused
      if (!hasInitialFocusRef.current) {
        e.preventDefault();
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          trap.focus();
        }
        hasInitialFocusRef.current = true;
        return;
      }

      if (focusables.length === 0) {
        e.preventDefault();
        trap.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!active || !trap.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (previousActive && typeof previousActive.focus === 'function' && document.contains(previousActive)) {
        previousActive.focus();
      }
    };
  }, []);

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      tabIndex={0}
      onClick={(e: any) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}
